<!--
  套餐管理页面组件
  功能：套餐的增删改查管理，仅超级管理员可访问
-->
<template>
  <div class="package-management-page" v-loading="loading">
    <el-page-header @back="goBack" class="page-header">
      <template #content>
        <span class="text-large font-600 mr-3">套餐管理</span>
      </template>
    </el-page-header>

    <!-- 操作栏 -->
    <el-card class="operation-card">
      <div class="operation-bar">
        <el-button
          type="primary"
          @click="showCreateDialog"
          v-if="hasPermission('package.manage')"
        >
          <el-icon><Plus /></el-icon>
          新增套餐
        </el-button>
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </el-card>

    <!-- 套餐列表 -->
    <el-card class="package-list-card">
      <el-table
        :data="packages"
        v-loading="tableLoading"
        stripe
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="套餐名称" width="150" />
        <el-table-column
          prop="monthly_free_minutes"
          label="每月免费通话时间"
          width="180"
        >
          <template #default="scope">
            <el-tag type="success"
              >{{ scope.row.monthly_free_minutes }} 分钟</el-tag
            >
          </template>
        </el-table-column>
        <el-table-column
          prop="recording_retention_days"
          label="录音保存天数"
          width="150"
        >
          <template #default="scope">
            <el-tag type="info"
              >{{ scope.row.recording_retention_days }} 天</el-tag
            >
          </template>
        </el-table-column>
        <el-table-column prop="price" label="价格" width="120">
          <template #default="scope">
            <span class="price-text">¥{{ scope.row.price }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="description"
          label="描述"
          min-width="200"
          show-overflow-tooltip
        />
        <el-table-column prop="is_active" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="scope.row.is_active ? 'success' : 'danger'">
              {{ scope.row.is_active ? "启用" : "禁用" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="180">
          <template #default="scope">
            {{ formatDateTime(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button
              size="small"
              type="primary"
              @click="showEditDialog(scope.row)"
              v-if="hasPermission('package.manage')"
            >
              编辑
            </el-button>
            <el-button
              size="small"
              type="danger"
              @click="handleDelete(scope.row)"
              v-if="hasPermission('package.manage')"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 套餐统计信息 -->
    <el-card class="statistics-card" v-if="statistics">
      <template #header>
        <span>套餐使用统计</span>
      </template>
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="stat-item">
            <div class="stat-number">{{ statistics.total_packages }}</div>
            <div class="stat-label">总套餐数</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-item">
            <div class="stat-number">{{ statistics.active_packages }}</div>
            <div class="stat-label">启用套餐数</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-item">
            <div class="stat-number">{{ statistics.total_devices }}</div>
            <div class="stat-label">使用设备数</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-item">
            <div class="stat-number">
              {{ statistics.packages_with_devices }}
            </div>
            <div class="stat-label">有设备套餐数</div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <!-- 新增/编辑套餐对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="packageFormRef"
        :model="packageForm"
        :rules="packageRules"
        label-width="140px"
      >
        <el-form-item label="套餐名称" prop="name">
          <el-input v-model="packageForm.name" placeholder="请输入套餐名称" />
        </el-form-item>
        <el-form-item label="每月免费通话时间" prop="monthly_free_minutes">
          <el-input-number
            v-model="packageForm.monthly_free_minutes"
            :min="1"
            :max="9999"
            placeholder="分钟"
          />
          <span class="form-hint">分钟</span>
        </el-form-item>
        <el-form-item label="录音保存天数" prop="recording_retention_days">
          <el-input-number
            v-model="packageForm.recording_retention_days"
            :min="1"
            :max="365"
            placeholder="天数"
          />
          <span class="form-hint">天</span>
        </el-form-item>
        <el-form-item label="价格" prop="price">
          <el-input-number
            v-model="packageForm.price"
            :min="0"
            :precision="2"
            :step="0.01"
            placeholder="价格"
          />
          <span class="form-hint">元</span>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="packageForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入套餐描述"
          />
        </el-form-item>
        <el-form-item label="状态" prop="is_active">
          <el-switch
            v-model="packageForm.is_active"
            active-text="启用"
            inactive-text="禁用"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button
            type="primary"
            @click="handleSubmit"
            :loading="submitLoading"
          >
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ElMessage, ElMessageBox } from "element-plus";
import { Plus, Refresh } from "@element-plus/icons-vue";
import type { FormInstance, FormRules } from "element-plus";
import apiClient from "@/services/apiClient";
import config from "@/config";

// 路由和状态管理
const router = useRouter();
const store = useStore();

// 响应式数据
const loading = ref(false);
const tableLoading = ref(false);
const submitLoading = ref(false);
const dialogVisible = ref(false);
const isEdit = ref(false);
const packages = ref([]);
const statistics = ref<any>({
  total_packages: 0,
  active_packages: 0,
  total_devices: 0,
  packages_with_devices: 0,
});
const currentPage = ref(1);
const pageSize = ref(20);
const total = ref(0);

// 表单相关
const packageFormRef = ref<FormInstance>();
const packageForm = reactive({
  id: null,
  name: "",
  monthly_free_minutes: 30,
  recording_retention_days: 30,
  price: 0.0,
  description: "",
  is_active: true,
});

// 表单验证规则
const packageRules: FormRules = {
  name: [
    { required: true, message: "请输入套餐名称", trigger: "blur" },
    {
      min: 2,
      max: 100,
      message: "套餐名称长度在 2 到 100 个字符",
      trigger: "blur",
    },
  ],
  monthly_free_minutes: [
    { required: true, message: "请输入每月免费通话时间", trigger: "blur" },
    { type: "number", min: 1, message: "通话时间必须大于0", trigger: "blur" },
  ],
  recording_retention_days: [
    { required: true, message: "请输入录音保存天数", trigger: "blur" },
    {
      type: "number",
      min: 1,
      max: 365,
      message: "保存天数在 1 到 365 天之间",
      trigger: "blur",
    },
  ],
  price: [
    { required: true, message: "请输入价格", trigger: "blur" },
    { type: "number", min: 0, message: "价格不能为负数", trigger: "blur" },
  ],
};

// 计算属性
const dialogTitle = computed(() => (isEdit.value ? "编辑套餐" : "新增套餐"));

// 权限检查
const hasPermission = (permission: string): boolean => {
  const userRole = store.getters.getUserRole;
  const permissions = store.getters.getUserPermissions || [];
  return permissions.includes(permission) || userRole === "super_admin";
};

// 返回上一页
const goBack = () => {
  router.go(-1);
};

// 格式化日期时间
const formatDateTime = (dateTime: string) => {
  if (!dateTime) return "N/A";
  return new Date(dateTime).toLocaleString("zh-CN");
};

// 获取套餐列表
const fetchPackages = async () => {
  try {
    tableLoading.value = true;
    const response = await apiClient.get(`${config.apiBaseUrl}/packages`, {
      params: {
        skip: (currentPage.value - 1) * pageSize.value,
        limit: pageSize.value,
      },
    });

    if (response.data.code === 0) {
      packages.value = response.data.data.items || [];
      total.value = response.data.data.total || 0;
    } else {
      ElMessage.error(response.data.message || "获取套餐列表失败");
    }
  } catch (error) {
    console.error("获取套餐列表失败:", error);
    ElMessage.error("获取套餐列表失败");
  } finally {
    tableLoading.value = false;
  }
};

// 获取套餐统计信息
const fetchStatistics = async () => {
  try {
    const response = await apiClient.get(
      `${config.apiBaseUrl}/packages/statistics`
    );
    if (response.data.code === 0) {
      statistics.value = response.data.data;
    } else {
      console.error("获取套餐统计失败:", response.data.message);
    }
  } catch (error) {
    console.error("获取套餐统计失败:", error);
  }
};

// 刷新数据
const refreshData = () => {
  fetchPackages();
  fetchStatistics();
};

// 显示新增对话框
const showCreateDialog = () => {
  isEdit.value = false;
  resetForm();
  dialogVisible.value = true;
};

// 显示编辑对话框
const showEditDialog = (pkg: any) => {
  isEdit.value = true;
  Object.assign(packageForm, pkg);
  dialogVisible.value = true;
};

// 重置表单
const resetForm = () => {
  Object.assign(packageForm, {
    id: null,
    name: "",
    monthly_free_minutes: 30,
    recording_retention_days: 30,
    price: 0.0,
    description: "",
    is_active: true,
  });
  packageFormRef.value?.clearValidate();
};

// 提交表单
const handleSubmit = async () => {
  if (!packageFormRef.value) return;

  try {
    await packageFormRef.value.validate();
    submitLoading.value = true;

    let response;
    if (isEdit.value) {
      response = await apiClient.put(
        `${config.apiBaseUrl}/packages/${packageForm.id}`,
        packageForm
      );
    } else {
      response = await apiClient.post(
        `${config.apiBaseUrl}/packages`,
        packageForm
      );
    }

    if (response.data.code === 0) {
      ElMessage.success(isEdit.value ? "套餐更新成功" : "套餐创建成功");
      dialogVisible.value = false;
      refreshData();
    } else {
      ElMessage.error(response.data.message || "操作失败");
    }
  } catch (error) {
    console.error("提交失败:", error);
    ElMessage.error("操作失败");
  } finally {
    submitLoading.value = false;
  }
};

// 删除套餐
const handleDelete = async (pkg: any) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除套餐"${pkg.name}"吗？此操作不可恢复。`,
      "确认删除",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    const response = await apiClient.delete(
      `${config.apiBaseUrl}/packages/${pkg.id}`
    );
    if (response.data.code === 0) {
      ElMessage.success("套餐删除成功");
      refreshData();
    } else {
      ElMessage.error(response.data.message || "删除失败");
    }
  } catch (error) {
    if (error !== "cancel") {
      console.error("删除失败:", error);
      ElMessage.error("删除失败");
    }
  }
};

// 分页处理
const handleSizeChange = (val: number) => {
  pageSize.value = val;
  currentPage.value = 1;
  fetchPackages();
};

const handleCurrentChange = (val: number) => {
  currentPage.value = val;
  fetchPackages();
};

// 页面加载
onMounted(() => {
  refreshData();
});
</script>

<style scoped>
.package-management-page {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.operation-card {
  margin-bottom: 20px;
}

.operation-bar {
  display: flex;
  gap: 12px;
  align-items: center;
}

.package-list-card {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.statistics-card {
  margin-bottom: 20px;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #409eff;
  margin-bottom: 8px;
}

.stat-label {
  color: #606266;
  font-size: 14px;
}

.price-text {
  font-weight: bold;
  color: #e6a23c;
}

.form-hint {
  margin-left: 8px;
  color: #909399;
  font-size: 14px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
